import { useQuery } from "@tanstack/react-query";
import LoadingScreen from "components/LoadingScreen";
import { getOrganizationCustomData } from "lib/organizationApi";
import SomethingWrong from "pages/FallbackPages/SomethingWrong";
import { ReactNode, createContext, useContext, useEffect } from "react";
import { useLocalStorage } from "usehooks-ts";

interface OrgContentData { [key: string]: any }
export interface OrgAssetsData { [key: string]: any }
export interface OrgCustomData {
  content: OrgContentData | null;
  assets: OrgAssetsData | null;
}
export interface OrgDataContextType {
  data: OrgCustomData;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  dataContent: OrgContentData;
  dataAssets: OrgAssetsData;
}

const CACHE_DURATION = 24 * 60 * 60 * 1000;
const ORG_DATA_STORAGE_KEY = "org_custom_data";

type StoredOrgData = OrgCustomData & { savedAt: number };

// Small helper hook that encapsulates localStorage cache handling
function useOrgDataCache(key: string, maxAgeMs: number) {
  const [cached, setCached] = useLocalStorage<StoredOrgData | null>(key, null);

  const now = Date.now();
  const isFresh = !!(cached && now - cached.savedAt < maxAgeMs);

  // Clear stale cache so consumers don't need to care
  useEffect(() => {
    if (cached && !isFresh) {
      localStorage.removeItem(key);
      setCached(null);
    }
  }, [cached, isFresh, key, setCached]);

  return {
    // Provide cached initial data for React Query when cache is fresh
    cachedInitialData: isFresh
      ? { content: cached!.content, assets: cached!.assets }
      : undefined as OrgCustomData | undefined,
    cachedUpdatedAt: isFresh ? cached!.savedAt : undefined,
    // Whether network fetch should run
    enabled: !isFresh,
    // Save new data back to cache with timestamp
    save: (fresh: OrgCustomData) => setCached({ ...fresh, savedAt: Date.now() }),
  };
}

const OrgDataContext = createContext<OrgDataContextType | undefined>(undefined);

export const OrgDataProvider = ({ children }: { children: ReactNode }) => {
  const { cachedInitialData, cachedUpdatedAt, enabled, save } = useOrgDataCache(
    ORG_DATA_STORAGE_KEY,
    CACHE_DURATION
  );

  const {
    data = { content: null, assets: null },
    isLoading,
    isError,
    error,
  } = useQuery<OrgCustomData, Error>({
    queryKey: ["orgData"],
    queryFn: async () => {
      const res = await getOrganizationCustomData();
      return {
        content: res?.data?.user_dashboard_content ?? {},
        assets: res?.data?.user_dashboard_assets ?? {},
      };
    },
    enabled,
    initialData: cachedInitialData,
    initialDataUpdatedAt: cachedUpdatedAt,
    staleTime: CACHE_DURATION,
    cacheTime: CACHE_DURATION,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    onSuccess: save,
  });

  if (isLoading) return <LoadingScreen />;
  if (isError) return <SomethingWrong />;

  const dataContent: OrgContentData = (data?.content ?? {}) as OrgContentData;
  const dataAssets: OrgAssetsData = (data?.assets ?? {}) as OrgAssetsData;

  const contextValue: OrgDataContextType = {
    data,
    isLoading,
    isError,
    error,
    dataContent,
    dataAssets,
  };

  return (
    <OrgDataContext.Provider value={contextValue}>
      {children}
    </OrgDataContext.Provider>
  );
};

export const useOrgData = (): OrgDataContextType => {
  const ctx = useContext(OrgDataContext);
  if (!ctx) throw new Error("useOrgData must be used within an OrgDataProvider");
  return ctx;
};
